/**
 * "At-home beauty service" cross-sell section. Shows other services flagged isCrossSell
 * (resolved in getStaticProps), excluding the current one, as eggshell cards. Prices hydrate
 * to the visitor's market via useMarketPrice. Renders a responsive grid on wide viewports and
 * switches to a horizontal arrow-nav carousel — the same track + scrollBy mechanism as
 * FeaturedAddons, contained within the page gutter — from tablet down. Matches the Figma card
 * (node 1491:3924).
 */
/* eslint-disable @next/next/no-img-element */
import React from 'react'
import Link from 'next/link'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq, formatDurationDisplay } from '../../helpers'
import { useMarketPrice } from '../../helpers/useMarketPrice'
import { bookServiceHref } from '../../helpers/bookLink'
import { useCarouselNav } from '../../helpers/useCarouselNav'
import { trackServiceDetailCrossSellClicked } from '../../helpers/tracking'

const MAX_CARDS = 6
const CARD_WIDTH = 294
const CARD_GAP = 16

/**
 * A single cross-sell card. Owns its own market-price hook so prices hydrate per card.
 * Links to the service's own detail page when it has one, else to the booking deep-link.
 * @param {{ service: object, fromSlug?: string }} props
 */
const CrossSellCard = ({ service, fromSlug }) => {
  const { priceText, market } = useMarketPrice(service)
  const href = service.slug ? `/services/${service.slug}` : bookServiceHref({ service, market })

  return (
    <article className="card">
      <h3>{service.name}</h3>
      {service.blurb && <p className="blurb">{service.blurb}</p>}
      <div className="foot">
        <div className="meta">
          {priceText && <span className="price">Starting at {priceText}</span>}
          {service.duration != null && (
            <span className="dur">
              <img src="/images/book-icon-time.svg" alt="" aria-hidden="true" width={18} height={19} />
              {formatDurationDisplay(service.duration, service)} mins
            </span>
          )}
        </div>
        <Link href={href} legacyBehavior>
          <a
            className="cta"
            onClick={() => trackServiceDetailCrossSellClicked({
              fromSlug,
              toSlug: service.slug,
              toServiceName: service.name,
            })}
          >
            Explore
          </a>
        </Link>
      </div>
    </article>
  )
}

/**
 * @param {{ services?: Array<{ id, slug, name, blurb, price, duration, serviceType }>, fromSlug?: string }} props
 */
const CrossSell = ({ services = [], fromSlug }) => {
  const cards = services.slice(0, MAX_CARDS)
  const { trackRef, scrollBy, atStart, atEnd, hasOverflow } = useCarouselNav((CARD_WIDTH + CARD_GAP) * 2, cards.length)

  if (!cards.length) {
    return null
  }

  return (
    <Wrapper>
      <div className="head">
        <h2>At-home beauty <br className="mobile-only" />service</h2>
        {hasOverflow && (
          <div className="nav">
            <button type="button" aria-label="Previous services" onClick={() => scrollBy(-1)} disabled={atStart}>
              <img src="/service-detail/arrow.svg" alt="" aria-hidden="true" width={18} height={18} />
            </button>
            <button type="button" aria-label="Next services" className="next" onClick={() => scrollBy(1)} disabled={atEnd}>
              <img src="/service-detail/arrow.svg" alt="" aria-hidden="true" width={18} height={18} />
            </button>
          </div>
        )}
      </div>
      <div className="grid" ref={trackRef}>
        {cards.map(s => <CrossSellCard key={s.id} service={s} fromSlug={fromSlug} />)}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  padding: 64px 88px;
  overflow: hidden;

  ${mq('max', '900px')} {
    padding: 40px 24px;
  }

  ${mq('max', '600px')} {
    padding: 40px 0;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 32px;
    ${mq('max', '600px')} {
      margin-bottom: 24px;
      padding: 0 24px;
    }
  }

  h2 {
    font-family: Garnett;
    font-size: 32px;
    font-weight: 600;
    line-height: 1.2;
    color: ${c.black};
    margin: 0;
    ${mq('max', '600px')} {
      font-size: 28px;
    }
    .mobile-only {
      display: none;
      ${mq('max', '600px')} {
        display: inline;
      }
    }
  }

  .nav {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
    ${mq('max', '600px')} {
      display: none;
    }
  }

  .nav button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${c.black};
  }
  .nav button.next img {
    transform: rotate(180deg);
  }
  .nav button:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${CARD_GAP}px;

    /* Tablet: 2 columns. */
    ${mq('max', '1024px')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${mq('max', '900px')} {
      display: flex;
      grid-template-columns: none;
      align-items: stretch;
      min-width: 0;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    ${mq('max', '600px')} {
      padding: 0 24px;
      scroll-padding-inline: 24px;
    }
  }
  .grid::-webkit-scrollbar {
    display: none;
  }

  .card {
    background: ${c.eggshell};
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 250px;

    ${mq('max', '900px')} {
      flex: 0 0 ${CARD_WIDTH}px;
      max-width: ${CARD_WIDTH}px;
      min-height: 0;
      padding: 24px 16px;
      scroll-snap-align: start;
    }
  }

  h3 {
    font-family: Euclid;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.4;
    color: ${c.black};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 20px;
    }
  }

  .blurb {
    font-family: Euclid;
    font-size: 16px;
    line-height: 1.45;
    letter-spacing: 0.16px;
    color: ${c.darkGrey};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 14px;
    }
  }

  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: auto;
  }

  .meta {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .price {
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.16px;
    color: ${c.black};
    ${mq('max', '900px')} {
      font-size: 14px;
      letter-spacing: 0.14px;
    }
  }

  .dur {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: Euclid;
    font-size: 16px;
    color: ${c.black};
    ${mq('max', '900px')} {
      font-size: 14px;
    }
  }

  .cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 114px;
    height: 48px;
    border: 1px solid ${c.black};
    color: ${c.black};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.16px;
    text-decoration: none;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background: ${c.black};
      color: ${c.white};
    }

    ${mq('max', '900px')} {
      width: 96px;
      height: 42px;
    }
  }
`

export default CrossSell
